<template>
  <div class="py-5 md:px-5">
    <Loading v-if="loading" class="py-12" />

    <template v-else>
      <PageHeader title="View Route" :subtitle="route.name" class="container" :back-route="{ name: 'RouteList' }">
        <button class="btn btn-secondary" v-submitting="downloading" @click="downloadRoute">
          <fa :icon="['far', 'arrow-to-bottom']" class="mr-2" /> Download
        </button>
      </PageHeader>
      
      <div class="container space-y-4">
        <!-- Details -->
        <div class="card">
          <div class="p-6 text-xl font-bold"> 
            Details
          </div>
          <div class="grid grid-cols-4 gap-10 px-4 pb-6 sm:px-6">
            <div>
              <div class="text-xs font-medium text-gray-500 uppercase truncate">
                Planned Start Time
              </div>
              <div class="font-medium">
                {{ route.planned_start_time | moment('HH:mm [on] Do MMM YYYY')}}
              </div>
              <div class="mt-2 text-xs font-medium text-gray-500 uppercase truncate">
                Planned End Time
              </div>
              <div class="font-medium">
                {{ route.planned_end_time | moment('HH:mm [on] Do MMM YYYY')}}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500 uppercase truncate">
                Start Depot
              </div>
              <div class="font-medium">
                {{ route.start_depot ? route.start_depot.name : 'N/A' }}
              </div>
              <div class="mt-2 text-xs font-medium text-gray-500 uppercase truncate">
                End Depot
              </div>
              <div class="font-medium">
                {{ route.end_depot ? route.end_depot.name : 'N/A' }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500 uppercase truncate">
                Vehicle Name
              </div>
              <div class="font-medium">
                {{ route.vehicle ? route.vehicle.name : 'N/A' }}
              </div>
              <div class="mt-2 text-xs font-medium text-gray-500 uppercase truncate">
                Vehicle Number
              </div>
              <div class="font-medium">
                {{ route.vehicle ? route.vehicle.objectno : 'N/A' }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500 uppercase truncate">
                Driver Name
              </div>
              <div class="font-medium">
                {{ route.driver ? route.driver.name : 'N/A' }}
              </div>
              <div class="mt-2 text-xs font-medium text-gray-500 uppercase truncate">
                Driver Number
              </div>
              <div class="font-medium">
                {{ route.driver ? route.driver.driverno : 'N/A' }}
              </div>
            </div>
          </div>
        </div>
        
        <!-- Summary -->
        <div class="card">
          <div class="p-5">
            <!-- Stops -->
            <div class="bg-gray-200">
              <dl class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      <span class="text-indigo-500">&#9679;</span>
                      Planned Stops
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ plannedStops }}
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      <span class="text-emerald-500">&#9679;</span>
                      Actual Stops
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ actualStops }}
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      <span class="text-rose-500">&#9679;</span>
                      Incomplete Stops
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ incompleteStops }}
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      <span class="text-yellow-500">&#9679;</span>
                      Unplanned Stops
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ unplannedStops }}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
            
            <!-- Metrics -->
            <div class="bg-gray-200">
              <dl class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      Planned Distance
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ (route.planned_distance / 1000).toFixed(2) }}km
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      Actual Distance
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ actualDistance }}
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      Sequence Compliance
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ sequenceCompliance }}
                    </dd>
                  </div>
                </div>

                <div class="p-6 bg-white">
                  <div class="flex-grow leading-tight">
                    <dt class="text-xs font-medium text-gray-500 uppercase truncate">
                      Driver Rating
                    </dt>
                    <dd class="text-3xl font-semibold text-gray-900">
                      {{ driverRating }}
                    </dd>
                  </div>
                </div>
              </dl>
            </div>
          </div>
        </div>
        
        <!-- Stops Detail -->
        <div class="overflow-hidden card">
          <div class="p-6 text-xl font-bold">
            Stops
          </div>
          <div>
            <div class="relative flex flex-col overflow-hidden">
              <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div class="inline-block min-w-full align-middle">
                  <div class="min-w-full">
                    <!-- Header -->
                    <div class="flex text-xs font-medium leading-4 tracking-wider text-gray-500 uppercase border-b bg-gray-50">
                      <div class="flex-none w-24 px-6 py-3">
                        Seq <br> 
                        (A / P)
                      </div>
                      <div class="flex-none px-6 py-3 w-72">
                        Customer <br>
                        Name
                      </div>
                      <div class="flex-none w-48 px-6 py-3">
                        Planned Arrival <br>
                        Actual Arrival
                      </div>
                      <div class="flex-none w-48 px-6 py-3">
                        Planned Departure <br>
                        Actual Departure
                      </div>
                      <div class="flex-none w-64 px-6 py-3">
                        Planned Turnaround <br>
                        Actual Turnaround
                      </div>
                      <div class="flex-none w-32 px-6 py-3 text-right">
                        Turnaround <br>
                        Variance
                      </div>
                      <div class="flex-grow w-32 px-6 py-3 text-right">
                        Notes
                      </div>
                    </div>

                    <!-- Items -->
                    <div class="bg-white">
                      <template v-for="(stop, index) in route.stops">
                        <div class="flex hover:bg-gray-50 group" :class="{ 'cursor-pointer': stop.notes.length > 0 }" :key="stop.id" @click="openStop === stop.id ? openStop = null : openStop = stop.id">
                          <div class="flex items-center justify-between w-24 px-6 py-2 text-sm border-b border-gray-100">
                            <div class="flex-none w-1/2 pr-1 font-bold text-left border-r">{{ index + 1 }}</div>  
                            <div class="flex-none w-1/2 text-right">{{ stop.planned_sequence }}</div>
                          </div>
                          <div class="flex items-center px-6 py-2 text-sm font-medium leading-tight border-b border-gray-100 w-72">
                            {{ stop.order.contact_company || stop.order.contact_name }}
                          </div>
                          <div class="w-48 px-6 py-2 text-sm border-b border-gray-100 ">
                            <div>
                              {{ stop.planned_arrival_time | moment('HH:mm') }}
                            </div>
                            <div>
                              {{ stop.actual_arrival_time | moment('HH:mm') || '-' }} 
                            </div>
                          </div>
                          <div class="w-48 px-6 py-2 text-sm border-b border-gray-100 ">
                            <div>
                              {{ stop.planned_departure_time | moment('HH:mm') }}
                            </div>
                            <div>
                              {{ stop.actual_departure_time | moment('HH:mm') || '-' }} 
                            </div>
                          </div>
                          <div class="w-64 px-6 py-2 text-sm border-b border-gray-100 ">
                            <div>
                              {{ msToMin(stop.planned_turnaround_time) }} 
                            </div>
                            <div>
                              {{ msToMin(stop.actual_turnaround_time) }}
                            </div>
                          </div>
                          <div class="flex items-center justify-end w-32 px-6 py-2 text-sm border-b border-gray-100">
                            <div class="px-1.5 py-0.5 text-xs font-medium rounded-md"
                              :class="{
                                'bg-green-100 text-green-500': stop.turnaround_time_variance <= 0,
                                'bg-red-100 text-red-500': stop.turnaround_time_variance > 0
                              }">
                              {{ stop.turnaround_time_variance > 0 ? '+' : '' }}{{ stop.turnaround_time_variance }}%
                            </div>
                          </div>
                          <div class="relative flex items-center justify-end flex-grow px-6 py-2 text-sm border-b border-gray-100">
                            <div class="px-3 py-0.5 text-xs opacity-25 font-medium bg-gray-100 rounded-full inline-flex items-center" :class="{ 'opacity-100': stop.notes.length }">
                              <fa :icon="['far', 'sticky-note']" class="mr-2 text-gray-500" /> 
                              {{ stop.notes.length }}
                            </div>
                            <div class="absolute inset-0 right-0 flex items-center justify-end w-full px-6 opacity-0 group-hover:opacity-100">
                              <button class="btn btn-sm btn-secondary"> 
                                Add Note
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Notes Panel -->
                        <Collapse :active="openStop === stop.id" :duration="200" :key="stop.id + '-notes'">
                          <div class="py-2 bg-gray-50">
                            <div v-for="note in stop.notes" :key="note.id" class="flex px-6 py-2">
                              <div class="flex-none w-12 text-xs text-gray-500">
                                {{ note.created_at | moment('HH:mm') }}
                              </div>
                              <div>
                                <div class="text-xs text-gray-500">
                                  {{ note.driver ? note.driver.name : note.user.name }}:
                                </div>
                                <div class="text-sm">
                                  {{ note.text }}
                                </div>
                              </div>
                            </div>
                            <div class="px-6 py-2">
                              <form @submit.prevent="addStopNote(stop)">
                                <fieldset>
                                  <textarea name="note" id="" cols="30" rows="2" class="max-w-xs form-textarea" v-model="stop.note"></textarea>
                                </fieldset>
                                <button class="btn btn-sm btn-primary" v-submitting="submitting">
                                  Add Note
                                </button>
                              </form>
                            </div>
                          </div>
                        </Collapse>

                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Exceptions -->
        <div class="overflow-hidden card">
          <div class="p-6 text-xl font-bold">
            Exceptions
          </div>
          <div>
            <data-table small :items="exceptions" 
              :keys="[
                { 
                  name: 'Type', 
                  slug: 'type', 
                  class: 'font-medium'
                },
                {
                  name: 'Value',
                  slug: 'value',
                  class: 'font-medium'
                }
              ]" />
          </div> 
        </div>

        <!-- Notes -->
        <div class="overflow-hidden card">
          <div class="p-6 text-xl font-bold">
            Notes
          </div>
          <div class="border-t divide-y" v-if="route.notes.length">
            <div v-for="note in route.notes" :key="note.id" class="p-6">
              <div class="text-sm text-gray-500">
                {{ note.user.name }} at {{ note.created_at | moment('HH:mm [on] DD/MM/YYYY') }}:
              </div>
              <div>
                {{ note.text }}
              </div>
            </div>
          </div>
          <div class="p-6 border-t">
            <form @submit.prevent="addRouteNote">
              <fieldset class="form-group">
                <label for="note" class="form-label">New Note</label>
                <textarea name="note" id="note" cols="30" rows="4" class="max-w-md form-textarea" v-model="note"></textarea>
              </fieldset>
              <button class="btn btn-primary" v-submitting="submitting">
                Add Note
              </button>
            </form>
          </div>
        </div>
      </div>
    </template>    
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
import * as ExcelJS from 'exceljs'
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'RouteView',

  created () {
    this.getRoute()
  },

  computed: {
    ...mapState(['automationTypes']),

    plannedStops () {
      return this.route.stops.filter(stop => stop.planned_sequence !== null).length
    },

    actualStops () {
      return this.route.stops.filter(stop => stop.actual_arrival_time !== null).length
    },
    
    incompleteStops () {
      return this.route.stops.filter(stop => stop.actual_arrival_time === null).length
    },
    
    unplannedStops () {
      return this.route.stops.filter(stop => stop.planned_sequence === null).length
    },

    actualDistance () {
      return this.metrics.actual_distance === 0 ? '...' : (this.metrics.actual_distance).toFixed(2) + 'km'
    },

    sequenceCompliance () {
      return this.metrics.sequence_compliance === 0 ? '...' : this.metrics.sequence_compliance + '%'
    },

    driverRating () {
      return this.metrics.driver_rating
    }
  },

  data () {
    return {
      moment,
      loading: true,
      downloading: false,
      submitting: false,
      syncing: false,
      openStop: null,
      route: {},
      metrics: {},
      exceptions: [],
      note: ''
    }
  },

  methods: {
    async getRoute () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/routes/${this.$route.params.id}`)
        this.route = data.route
        this.metrics = data.metrics
        this.exceptions = data.exceptions
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async addStopNote (stop) {
      try {
        this.submitting = true
        await this.$api.post(`account/${this.$route.params.account}/stops/${stop.id}/notes`, {
          text: stop.note
        })
        await this.getRoute()
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async addRouteNote () {
      try {
        this.submitting = true
        await this.$api.post(`account/${this.$route.params.account}/routes/${this.$route.params.id}/notes`, {
          text: this.note
        })
        await this.getRoute()
        this.note = ''
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async downloadRoute () {
      const filename = `Route Report ${this.route.name} ${this.formatDate(this.route.planned_start_time, 'YYYY-MM-DD')}.xlsx`
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Route')

      worksheet.addRow([`Start Depot`, `${this.route.start_depot.name}`])
      worksheet.addRow([`End Depot`, `${this.route.end_depot.name}`])
      worksheet.addRow([`Driver Name`, `${this.route.driver.name}`])
      worksheet.addRow([`Route Name`, `${this.route.name}`])
      worksheet.addRow([`Vehicle Number`, `${this.route.vehicle.objectno}`])
      worksheet.addRow([`Vehicle Registration`, `${this.route.vehicle.licenseplatenumber}`])
      worksheet.addRow([`Route Start`, `${this.formatDate(this.route.planned_start_time, 'HH:mm DD/MM/YYYY')}`])
      worksheet.addRow([`Route End`, `${this.formatDate(this.route.planned_end_time, 'HH:mm DD/MM/YYYY')}`])
      worksheet.addRow([])
      worksheet.addRow([`Planned Stops`, `${this.plannedStops}`])
      worksheet.addRow([`Actual Stops`, `${this.actualStops}`])
      worksheet.addRow([`Incomplete Stops`, `${this.incompleteStops}`])
      worksheet.addRow([`Unplanned Stops`, `${this.unplannedStops}`])
      worksheet.addRow([])
      worksheet.addRow([`Seq`, `Customer`, `Planned Arrival Time`, `Actual Arrival Time`, `Planned Turnaround`, `Actual Turnaround`, `Notes`])


      for (let stop of this.route.stops) {
        let notes = ``

        for (let note of stop.notes) {
          notes += `${this.formatDate(note.created_at, 'HH:mm')}] ${note.driver.name || note.user.name}: ${note.text} \n`
        }

        worksheet.addRow([
          `${stop.planned_sequence}`, 
          `${stop.order.contact_company ? stop.order.contact_company : stop.order.contact_name || '-'}`, 
          `${this.formatDate(stop.planned_arrival_time, 'HH:mm')}`, 
          `${this.formatDate(stop.actual_arrival_time, 'HH:mm')}`, 
          `${this.msToMin(stop.planned_turnaround_time) || ''}`, 
          `${this.msToMin(stop.actual_turnaround_time) || ''}`,
          `${notes}`
        ])
      }

      worksheet.addRow([])
      worksheet.addRow([`Planned Distance`, `${(this.route.planned_distance / 1000).toFixed(2)}km`])
      worksheet.addRow([`Actual Distance`, `${this.actualDistance}`])
      worksheet.addRow([`Sequence Compliance`, `${this.sequenceCompliance}`])
      worksheet.addRow([`Driver Rating`, `${this.driverRating}`])
      worksheet.addRow([])
      
      for (let exception of this.exceptions) {
        worksheet.addRow([`${exception.type || ''}`, `${exception.count || ''}`])
      }

      worksheet.getRow(15).font = { bold: true }

      worksheet.getColumn(1).font = { bold: true }
      worksheet.getColumn(1).width = 25
      worksheet.getColumn(2).width = 25
      worksheet.getColumn(3).width = 15
      worksheet.getColumn(4).width = 15
      worksheet.getColumn(5).width = 15
      worksheet.getColumn(6).width = 15

      workbook.xlsx.writeBuffer()
        .then((buffer) => {
          saveAs(new Blob([buffer]), filename)
        })
        .catch((error) => {
          console.log('Error writing excel export', error)
        })
    },

    formatDate (date, format) {
      if (moment(date).isValid()) {
        return moment(date).format(format)
      } else {
        return '-'
      }
    },

    msToMin (duration) {
      if (!duration) {
        return `-`
      }

      let seconds = Math.floor((duration / 1000) % 60),
        minutes = Math.floor((duration / (1000 * 60)) % 60),
        hours = Math.floor((duration / (1000 * 60 * 60)) % 24)

      let string = ''

      hours > 0 ? string += `${hours}h ` : ''
      minutes > 0 ? string += `${minutes}m ` : ''
      string += `${seconds}s`
      
      return string
    }
  }
}
</script>